import { useMemo, useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { formatDateTime } from '../lib/format'
import { pagePath } from '../lib/pages'
import { useAuth } from '../lib/auth'
import { AUFBEWAHRUNG, aufbewahrungText, schwereStufe, schwereText } from '../lib/syslog'
import type { Customer, Device, SyslogPage as SyslogSeite } from '../lib/types'
import { EmptyState, ErrorBox, Loading } from '../components/Feedback'
import { StatusBadge } from '../components/Status'

interface Props {
  customerSlug: string | null
}

const ZEITRAEUME: [string, string, number][] = [
  ['1h', '1 Stunde', 1],
  ['24h', '24 Stunden', 24],
  ['7d', '7 Tage', 24 * 7],
  ['30d', '30 Tage', 24 * 30],
]

const SCHWERE_FILTER: [number, string][] = [
  [7, 'Alle'],
  [6, 'ab Info'],
  [5, 'ab Hinweis'],
  [4, 'ab Warnung'],
  [3, 'nur Fehler'],
]

/**
 * Syslog der Geräte: was sie selbst melden, durchsuchbar nach Zeitraum,
 * Schwere, Gerät und Text. Gesammelt wird nur für Kunden, die eine
 * Aufbewahrung gewählt haben.
 */
export function SyslogPage({ customerSlug }: Props) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const geraetAusAdresse = useRouterState({
    select: (s) => (s.location.search as Record<string, unknown>).geraet,
  })
  const [zeitraum, setZeitraum] = useState('24h')
  const [schwere, setSchwere] = useState(7)
  const [geraet, setGeraet] = useState(typeof geraetAusAdresse === 'string' ? geraetAusAdresse : '')
  const [eingabe, setEingabe] = useState('')
  const [suche, setSuche] = useState('')

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
  })
  const customer = customerSlug ? customers.data?.find((c) => c.slug === customerSlug) : undefined
  const devices = useQuery({
    queryKey: ['devices', customer?.id ?? null, ''],
    queryFn: ({ signal }) => apiGet<Device[]>(`/devices${customer ? `?kunde=${customer.id}` : ''}`, signal),
    enabled: !customerSlug || Boolean(customer),
  })

  const parameter = useMemo(() => {
    const stunden = ZEITRAEUME.find(([k]) => k === zeitraum)?.[2] ?? 24
    const p = new URLSearchParams()
    if (customer) p.set('kunde', customer.id)
    if (geraet) p.set('geraet', geraet)
    p.set('schwere', String(schwere))
    if (suche) p.set('suche', suche)
    // Die Zeit wird beim Abfragen gesetzt, nicht hier: sonst wanderte der
    // Schlüssel der Abfrage mit jeder Sekunde.
    return { p: p.toString(), stunden }
  }, [customer, geraet, schwere, suche, zeitraum])

  const liste = useInfiniteQuery({
    queryKey: ['syslog', parameter.p, parameter.stunden],
    enabled: !customerSlug || Boolean(customer),
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) => {
      const p = new URLSearchParams(parameter.p)
      const bis = new Date()
      p.set('von', new Date(bis.getTime() - parameter.stunden * 3_600_000).toISOString())
      p.set('bis', bis.toISOString())
      if (pageParam) p.set('weiter', pageParam)
      return apiGet<SyslogSeite>(`/syslog?${p.toString()}`, signal)
    },
    getNextPageParam: (last) => (last.weitere ? last.weiter : undefined),
    refetchInterval: 30_000,
  })

  const zeilen = liste.data?.pages.flatMap((p) => p.eintraege) ?? []
  const verworfen = liste.data?.pages[0]?.verworfen ?? 0
  const tage = customer?.syslog_retention_days ?? 0
  const kundeOhneSyslog = customer && tage === 0

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Syslog</h1>
          <div className="page-sub">
            {customer?.name ?? 'Alle Kunden'} · {zeilen.length}
            {liste.hasNextPage ? '+' : ''} Meldungen
            {customer ? ` · Aufbewahrung ${aufbewahrungText(tage)}` : ''}
          </div>
        </div>
      </div>

      {customer && can('settings.manage') ? (
        <Aufbewahrung
          customer={customer}
          onGespeichert={() => void queryClient.invalidateQueries({ queryKey: ['customers'] })}
        />
      ) : null}

      <section className="card">
        <form
          className="card-head"
          style={{ flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault()
            setSuche(eingabe.trim())
          }}
        >
          <div className="field">
            <label htmlFor="syslog-zeitraum">Zeitraum</label>
            <select
              id="syslog-zeitraum"
              className="input"
              value={zeitraum}
              onChange={(e) => setZeitraum(e.target.value)}
            >
              {ZEITRAEUME.map(([k, text]) => (
                <option key={k} value={k}>
                  {text}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="syslog-schwere">Schwere</label>
            <select
              id="syslog-schwere"
              className="input"
              value={schwere}
              onChange={(e) => setSchwere(Number(e.target.value))}
            >
              {SCHWERE_FILTER.map(([n, text]) => (
                <option key={n} value={n}>
                  {text}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ minWidth: 200 }}>
            <label htmlFor="syslog-geraet">Gerät</label>
            <select id="syslog-geraet" className="input" value={geraet} onChange={(e) => setGeraet(e.target.value)}>
              <option value="">Alle Geräte</option>
              {(devices.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {customerSlug ? '' : ` (${d.customer_name ?? ''})`}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ minWidth: 240, flex: 1 }}>
            <label htmlFor="syslog-suche">Text</label>
            <input
              id="syslog-suche"
              className="input"
              value={eingabe}
              maxLength={200}
              placeholder="z. B. link down, admin, Volume"
              onChange={(e) => setEingabe(e.target.value)}
            />
          </div>
          <button type="submit" className="btn">
            Suchen
          </button>
        </form>

        {verworfen > 0 ? (
          <div className="card-body" style={{ color: 'var(--warn)', fontSize: 13 }}>
            {verworfen} Meldungen haben die Agents im Zeitraum verworfen, weil ein Gerät mehr als 20 je Sekunde sandte.
            Das sind meist Verkehrs-Logs einer Firewall; für die Suche nach Ereignissen genügen die Ereignis-Logs.
          </div>
        ) : null}

        {liste.isError ? (
          <div className="card-body">
            <ErrorBox error={liste.error} what="Das Syslog" />
          </div>
        ) : liste.isPending ? (
          <div className="card-body">
            <Loading what="Syslog" />
          </div>
        ) : zeilen.length === 0 ? (
          <EmptyState
            title="Keine Meldungen im Zeitraum"
            hint={
              kundeOhneSyslog
                ? 'Für diesen Kunden wird Syslog nicht gespeichert. Oben eine Aufbewahrung wählen.'
                : 'Gesammelt wird, wenn der Kunde eine Aufbewahrung gewählt hat, am Agent der Syslog-Empfang an ist (Standorte & Agents) und die Geräte an die Adresse des Agents senden (UDP 514).'
            }
          />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Zeit</th>
                <th>Gerät</th>
                <th>Schwere</th>
                <th>Programm</th>
                <th>Meldung</th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z) => (
                <tr key={z.id}>
                  <td className="mono" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {formatDateTime(z.time)}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Link to={`${pagePath('geraete', z.customer_slug || customerSlug)}/${z.device_id}`}>
                      {z.device_name}
                    </Link>
                  </td>
                  <td>
                    <StatusBadge state={schwereStufe(z.severity)} label={schwereText(z.severity)} />
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {z.app || '–'}
                  </td>
                  <td className="mono" style={{ fontSize: 12, wordBreak: 'break-word' }}>
                    {z.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {liste.hasNextPage ? (
          <div className="card-body">
            <button
              type="button"
              className="btn"
              disabled={liste.isFetchingNextPage}
              onClick={() => void liste.fetchNextPage()}
            >
              Ältere laden
            </button>
          </div>
        ) : null}
      </section>
    </>
  )
}

function Aufbewahrung({ customer, onGespeichert }: { customer: Customer; onGespeichert: () => void }) {
  const aktuell = customer.syslog_retention_days ?? 0
  const [tage, setTage] = useState(aktuell)
  const speichern = useMutation({
    mutationFn: () => apiSend('PUT', `/customers/${customer.id}/syslog`, { aufbewahrung_tage: tage }),
    onSuccess: onGespeichert,
  })
  return (
    <section className="card">
      <form
        className="card-body row"
        style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}
        onSubmit={(e) => {
          e.preventDefault()
          speichern.mutate()
        }}
      >
        <div className="field">
          <label htmlFor="syslog-aufbewahrung">Syslog von {customer.name}</label>
          <select
            id="syslog-aufbewahrung"
            className="input"
            value={tage}
            onChange={(e) => setTage(Number(e.target.value))}
          >
            {AUFBEWAHRUNG.map((t) => (
              <option key={t} value={t}>
                {t === 0 ? 'nicht speichern' : `${t} Tage aufbewahren`}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className="btn" disabled={tage === aktuell || speichern.isPending}>
          Aufbewahrung speichern
        </button>
        <span className="muted" style={{ fontSize: 12, flex: 1, minWidth: 240 }}>
          {tage === 0 && aktuell > 0
            ? 'Beim Abschalten werden die gespeicherten Meldungen mit der nächsten Bereinigung gelöscht.'
            : 'Ältere Meldungen löscht die Zentrale von selbst. Syslog kann Benutzernamen und Adressen enthalten.'}
        </span>
        {speichern.isError ? <span style={{ color: 'var(--crit)' }}>{(speichern.error as Error).message}</span> : null}
      </form>
    </section>
  )
}
